import { FC, ReactNode } from 'react';
import { Box, Typography, BoxProps } from '@mui/material';

/**
 * Shared workspace rhythm.
 *
 * Workspace padding        : 3 (24px) / 4 (32px) up-md — defined on Styled.Content
 * Section header → content : 3 (24px)
 * Section → section        : 4 (32px)
 * Controls → table         : 3 (24px)
 * Form column/row gap      : 2 (16px)
 */

type SettingsSectionHeaderProps = {
    title: string;
    description?: string;
    action?: ReactNode;
} & Omit<BoxProps, 'title' | 'children'>;

const SettingsSectionHeader: FC<SettingsSectionHeaderProps> = ({
    title,
    description,
    action,
    sx,
    ...boxProps
}) => (
    <Box
        {...boxProps}
        sx={[
            {
                mb: 3,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.5,
            },
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
    >
        <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
            <Typography
                variant="subtitle1"
                className="font-yekan-heavy"
                sx={{ color: 'text.primary' }}
            >
                {title}
            </Typography>

            {description && (
                <Typography
                    variant="body2"
                    sx={{ mt: 0.5, color: 'text.secondary' }}
                >
                    {description}
                </Typography>
            )}
        </Box>

        {action && (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexShrink: 0,
                }}
            >
                {action}
            </Box>
        )}
    </Box>
);

export { SettingsSectionHeader };
